import React from 'react';
import {
  Box,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { getFlatPortfolioColors, portfolioTypography } from '../../../utils/getFlatPortfolioColors';
import PageSizeSelector from './PageSizeSelector';

export interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  startIndex: number;
  endIndex: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onNextPage: () => void;
  onPreviousPage: () => void;
  // Page size props
  pageSize?: number;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
}

const PaginationControls: React.FC<PaginationControlsProps> = ({
  currentPage,
  totalPages,
  hasNextPage,
  hasPreviousPage,
  onNextPage,
  onPreviousPage,
  pageSize,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50],
  totalItems,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const colors = getFlatPortfolioColors(theme);

  // Don't render if there's only one page and no page size options
  if (totalPages <= 1 && !onPageSizeChange) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 1.5,
        px: 2,
        borderTop: `1px solid ${colors.border.secondary}`,
        backgroundColor: colors.background.primary,
      }}
    >
      {/* Page size selector */}
      {pageSize && onPageSizeChange && (
        <PageSizeSelector
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
          totalItems={totalItems}
          options={pageSizeOptions}
        />
      )}
      {!pageSize && <Box />}

      {/* Navigation controls */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={onPreviousPage}
            disabled={!hasPreviousPage}
            size="small"
            sx={{
              color: hasPreviousPage ? colors.text.secondary : colors.text.muted,
              backgroundColor: 'transparent',
              borderRadius: 1,
              p: 0.5,

              '&:hover': {
                backgroundColor: hasPreviousPage ? colors.background.tertiary : 'transparent',
              },

              '&:disabled': {
                color: colors.text.muted,
              },
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 20 }} />
          </IconButton>

          <Typography
            sx={{
              ...portfolioTypography.caption,
              color: colors.text.secondary,
              minWidth: 60,
              textAlign: 'center',
            }}
          >
            {currentPage} / {totalPages}
          </Typography>

          <IconButton
            onClick={onNextPage}
            disabled={!hasNextPage}
            size="small"
            sx={{
              color: hasNextPage ? colors.text.secondary : colors.text.muted,
              backgroundColor: 'transparent',
              borderRadius: 1,
              p: 0.5,

              '&:hover': {
                backgroundColor: hasNextPage ? colors.background.tertiary : 'transparent',
              },

              '&:disabled': {
                color: colors.text.muted,
              },
            }}
          >
            <ChevronRightIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      )}
      {totalPages <= 1 && <Box />}
    </Box>
  );
};

export default PaginationControls;
